import React, { useMemo, useEffect, useState, useCallback } from "react";
import { useWindowScroll, useWindowSize } from "react-use";
import classNames from "classnames";
import MapContainer, { createFitBounds } from "../route-explorer/MapContainer";
import StickyWithOffset from "../layout/StickyWithOffset";
import lineString from "turf-linestring";
import find from "lodash.find";
import { getNearestStopId } from "../layout/utils";
import { useVhCssVar } from "../atlas/AtlasStopMap";
import { useBreakpointsContext } from "../../hooks";
import loadable from "@loadable/component";

const LoadableGeoJSONLayer = loadable(async () => {
    const { GeoJSONLayer } = await import(/* webpackChunkName: "ReactMapboxGl" */ "react-mapbox-gl");
    return GeoJSONLayer;
});
let timeoutId = null;

export function getPlannerMapMarkers({ itinerary, loopDirection, isSecondWayVisible, activeStopId, startingPoint }) {
    if (!itinerary) {
        return [];
    }
    let stops = itinerary.itineraryData.stops.filter(stop => stop.type.toLowerCase() === "stop");
    const midwayStopIndex = stops.findIndex(stop => stop.isMidwayPoint === true);
    const hasPlace = itinerary.itineraryData.stops.find(stop => stop.type === "place_start");
    if (
        midwayStopIndex === -1 &&
        startingPoint?.coordinates?.length &&
        itinerary.routeData.routeType?.toUpperCase() === "LOOP" &&
        !hasPlace
    ) {
        const startStop = find(stops, {
            id: getNearestStopId(stops, startingPoint.coordinates),
        });
        const startIndex = stops.findIndex(item => item.id === startStop.id);
        stops = [...stops.slice(startIndex), ...stops.slice(0, startIndex)].map((item, index) => ({
            ...item,
            order: index,
        }));
    }
    let result = stops.map((stop, index, stops) => {
        const { lat: latitude, lon: longitude } = stop.coords;
        const isEnd = loopDirection === "COUNTER_CLOCKWISE" ? index === 1 : index === stops.length - 1;
        const isStart = index === 0;
        const isMidway = stop.isMidwayPoint === true;
        return {
            longitude,
            latitude,
            id: stop.id,
            showTooltip: (isEnd || isStart || isMidway) && stop.id === activeStopId,
            tooltipLabel: isMidway ? "Midway" : isStart ? "Start" : "End",
            label: index + 1,
        };
    });
    if (isSecondWayVisible) {
        return result.slice(midwayStopIndex);
    } else if (midwayStopIndex >= 0) {
        return result.slice(0, midwayStopIndex + 1);
    }
    return loopDirection === "COUNTER_CLOCKWISE"
        ? [
              result[0],
              ...result
                  .slice(1)
                  .reverse()
                  .map((marker, index) => ({ ...marker, label: index + 2 })),
          ]
        : result;
}
const fillPaint = {
    "fill-color": "#504cf3",
    "fill-opacity": 0.25,
};

function PlannerMap(props) {
    const {
        isMapOverlayVisible,
        mapOverlayText,
        className,
        itinerary,
        viewport,
        setViewport,
        activeStopId,
        stopIdsByPlaceStartId,
        isAutoFollowEnabled,
        setIsAutoFollowEnabled,
        isSecondWayVisible,
        loopDirection,
        onMarkerClick,
        appHeaderHeight,
        startingPoint,
        stopsById,
        useTwoColumnLayout,
        sizeByStickyContent,
        setSizeByStickyContent,
        cardsOffsetTop,
        activeSuggestionId,
    } = props;
    const { isDesktop } = useBreakpointsContext();
    const [isMapSticky, setIsMapSticky] = useState(false);
    const mapWrapperHeight = "260px";
    const mapStyle = useMemo(() => {
        return {
            borderBottom: "1px solid #D5D5D5",
            height: mapWrapperHeight,
        };
    }, []);
    const mapWrapperStyle = useMemo(() => {
        return {
            height: mapWrapperHeight,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            backgroundColor: "#efe9e1",
        };
    }, []);
    const mapHeight = useTwoColumnLayout ? `calc(100vh - ${appHeaderHeight}px)` : "100%";
    const { windowWidth } = useWindowSize();
    const { y: windowY } = useWindowScroll();
    const dayComputedHeight = useMemo(() => {
        if (windowWidth >= 500) {
            return 64;
        }
        return 52;
    }, [windowWidth]);
    const stickyMapComputedHeight = 260;
    const activeMarkerIds = useMemo(() => [activeStopId], [activeStopId]);
    const markers = useMemo(() => {
        return getPlannerMapMarkers({
            itinerary,
            loopDirection,
            isSecondWayVisible,
            activeStopId,
            startingPoint,
        });
    }, [itinerary, activeStopId, isSecondWayVisible, loopDirection, startingPoint]);
    const topOffset = appHeaderHeight + (useTwoColumnLayout ? 0 : dayComputedHeight);
    const routePath = itinerary?.routeData?.routePath;
    const activeSuggestionPlace = useMemo(() => {
        if (!stopsById[activeSuggestionId]) {
            return;
        }
        return stopsById[activeSuggestionId].placeOutline;
    }, [stopsById, activeSuggestionId]);
    const fitBounds = useMemo(() => {
        if (!markers.length) {
            return () => {};
        }
        if (stopIdsByPlaceStartId[activeStopId]?.length) {
            const geojsonObject = lineString(
                stopIdsByPlaceStartId[activeStopId].map(stopId => [
                    stopsById[stopId].coords.lon,
                    stopsById[stopId].coords.lat,
                ]),
            );
            return createFitBounds(geojsonObject);
        }
        if (activeStopId) {
            return ({ onViewportChange }) => {
                const coords = stopsById[activeStopId].coords;
                if (!coords) {
                    return;
                }
                onViewportChange({
                    latitude: coords.lat,
                    longitude: coords.lon,
                    zoom: 10,
                });
            };
        }
        const points = markers.map(marker => [marker.longitude, marker.latitude]);
        const geojsonObject = lineString([...points, ...(routePath?.features?.[0]?.geometry?.coordinates ?? [])]);
        return createFitBounds(geojsonObject);
        // HACK: markers change on scrolling
        // TODO: refactor markers so it doesn't change on scrolling
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itinerary, routePath, stopsById, stopIdsByPlaceStartId, activeStopId]);
    const mainFitBounds = useMemo(() => {
        if (!markers.length) {
            return () => {};
        }
        const points = markers.map(marker => [marker.longitude, marker.latitude]);
        const geojsonObject = lineString([...points, ...(routePath?.features?.[0]?.geometry?.coordinates ?? [])]);
        return createFitBounds(geojsonObject);
        // HACK: markers change on scrolling
        // TODO: refactor markers so it doesn't change on scrolling
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itinerary, routePath]);

    //Add --vh CSS var
    useVhCssVar();

    //Update sizeByStickyContent
    useEffect(() => {
        if (
            sizeByStickyContent.dayComputedHeight !== dayComputedHeight ||
            sizeByStickyContent.stickyMapComputedHeight !== stickyMapComputedHeight
        ) {
            setSizeByStickyContent({
                dayComputedHeight,
                stickyMapComputedHeight,
            });
        }
        // eslint-disable-next-line
    }, [dayComputedHeight, stickyMapComputedHeight, setSizeByStickyContent]);

    const reCenter = useCallback(() => {
        const mapboxEl = document.querySelector(".js__mapbox-gl");
        if (!mapboxEl) {
            return;
        }
        const mapSize = mapboxEl.getBoundingClientRect();
        if (isMapSticky) {
            fitBounds({ mapSize, onViewportChange: setViewport, useOldLayout: false, useTwoColumnLayout });
        } else {
            mainFitBounds({ mapSize, onViewportChange: setViewport, useOldLayout: false, useTwoColumnLayout });
        }
    }, [setViewport, useTwoColumnLayout, fitBounds, mainFitBounds, isMapSticky]);

    //Make map sticky
    //making the map sticky will change its height
    useEffect(() => {
        const dayEl = document.querySelector(".js__itinerary-day");
        if (!dayEl) {
            return;
        }
        const { y: nodeY } = dayEl.getBoundingClientRect();
        const offset = cardsOffsetTop;

        if (nodeY <= offset) {
            //Stop card is in scroll view
            if (!isMapSticky) {
                setIsMapSticky(true);
            }
        } else {
            if (isMapSticky) {
                setIsMapSticky(false);
            }
        }
        //eslint-disable-next-line
    }, [windowY]);

    useEffect(() => {
        //HACK: center first stop correctly
        if (
            isAutoFollowEnabled &&
            !useTwoColumnLayout &&
            ((isMapSticky && activeStopId) || (!isMapSticky && !activeStopId))
        ) {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(reCenter, 300);
        }
        //eslint-disable-next-line
    }, [isMapSticky, activeStopId]);

    const mapTemplate = (
        <MapContainer
            isDesktop={isDesktop}
            isMapOverlayVisible={isMapOverlayVisible}
            mapOverlayText={mapOverlayText}
            fitBounds={fitBounds}
            mainFitBounds={mainFitBounds}
            viewport={viewport}
            onViewportChange={setViewport}
            markers={markers}
            activeMarkerIds={activeMarkerIds}
            activeSecondaryMarkerIds={stopIdsByPlaceStartId[activeStopId]}
            isAutoFollowEnabled={isAutoFollowEnabled}
            setIsAutoFollowEnabled={setIsAutoFollowEnabled}
            showAutoFollow={true}
            mapHeight={mapHeight}
            onMarkerClick={onMarkerClick}
            inItinerary={true}
            markerClasses={`trpr__marker--stop-atlas ${
                useTwoColumnLayout ? "trpr__marker--with-two-columns-layout" : ""
            }`}
            withMapControls={true}
            useTwoColumnLayout={useTwoColumnLayout}
            useOldLayout={false}
            style={{ height: !useTwoColumnLayout ? "100%" : null }}
            reCenterOnResize={useTwoColumnLayout}
        >
            {routePath && (
                <LoadableGeoJSONLayer
                    data={routePath}
                    linePaint={{
                        "line-color": "rgba(80, 76, 243, 0.75)",
                        "line-width": 3,
                    }}
                />
            )}
            {activeSuggestionPlace && <LoadableGeoJSONLayer data={activeSuggestionPlace} fillPaint={fillPaint} />}
        </MapContainer>
    );

    return (
        <div
            className={classNames(className, "trpr__route-explorer-map", "in-itinerary", "trpr-planner-map")}
            style={
                !useTwoColumnLayout
                    ? {
                          top: topOffset,
                          position: "sticky",
                          zIndex: 5,
                      }
                    : {}
            }
        >
            {useTwoColumnLayout ? (
                <StickyWithOffset
                    topOffset={topOffset}
                    render={({ style, ref, isSticky }) => (
                        <div
                            ref={ref}
                            style={{
                                ...style,
                                zIndex: 3,
                                overflow: "hidden",
                            }}
                        >
                            {mapTemplate}
                        </div>
                    )}
                />
            ) : (
                <div style={mapWrapperStyle}>
                    <div style={mapStyle}>{mapTemplate}</div>
                </div>
            )}
        </div>
    );
}

export default PlannerMap;
